<template>
  <section class="invoice-management-page">
    <header class="invoice-page-header d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4">
      <div>
        <p class="eyebrow mb-2">
          Finance operations
        </p>
        <h1 class="mb-1">
          Invoice History
        </h1>
        <p class="text-muted mb-0">
          Browse all generated invoices with pagination, filters, and PDF download.
        </p>
      </div>
    </header>

    <invoice-filter-bar
      :companies-url="'/admin/invoice/history/companies'"
      :result-count="totalInvoices"
      :show-date-filter="true"
      :date-period-label="'Issue date'"
      :show-status-filter="true"
      @filter-change="onFilterChange"
    />

    <div
      v-if="loading"
      class="text-center py-5"
    >
      <div
        class="spinner-border text-primary"
        role="status"
      >
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <template v-else-if="invoices.length > 0">
      <div class="history-table-card card border-0 shadow-sm">
        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th style="width: 40px;">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="allSelected"
                    :disabled="bulkSaving"
                    :indeterminate.prop="someSelected"
                    @change="toggleSelectAll($event.target.checked)"
                  >
                </th>
                <th style="width: 40px;" />
                <th>Invoice #</th>
                <th>Status</th>
                <th>Project</th>
                <th>Client</th>
                <th>Issue Date</th>
                <th>Due Date</th>
                <th>Amount</th>
                <th>Created By</th>
                <th class="text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="invoice in invoices">
                <tr
                  :key="invoice.id"
                  :class="{ 'invoice-row-expanded': isInvoiceExpanded(invoice.id) }"
                >
                  <td class="expand-cell">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :value="invoice.id"
                      :checked="selectedInvoiceIds.includes(invoice.id)"
                      :disabled="bulkSaving"
                      @change="toggleRowSelect(invoice.id, $event.target.checked)"
                    >
                  </td>
                  <td class="expand-cell">
                    <button
                      v-if="invoice.items && invoice.items.length > 0"
                      type="button"
                      class="btn btn-sm btn-link expand-toggle p-0"
                      :aria-expanded="isInvoiceExpanded(invoice.id) ? 'true' : 'false'"
                      :title="isInvoiceExpanded(invoice.id) ? 'Hide items' : 'Show items'"
                      @click="toggleInvoiceExpand(invoice.id)"
                    >
                      <i
                        :class="isInvoiceExpanded(invoice.id) ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"
                        class="chevron"
                      />
                    </button>
                  </td>
                  <td class="fw-semibold">
                    {{ invoice.invoice_number_formatted }}
                  </td>
                  <td>
                    <select
                      class="form-select form-select-sm invoice-status-select"
                      :value="invoice.invoice_status"
                      :disabled="bulkSaving || !!savingStatusIds[invoice.id]"
                      @change="onStatusChange(invoice, $event)"
                    >
                      <option value="">
                        Unpaid
                      </option>
                      <option value="send_to_client">
                        Sent to Client
                      </option>
                      <option value="paid">
                        Paid
                      </option>
                    </select>
                  </td>
                  <td>
                    <a
                      v-if="invoice.project_id"
                      :href="getProjectUrl(invoice.project_id)"
                      target="_blank"
                      class="project-link"
                    >
                      {{ invoice.project_title }}
                    </a>
                    <span v-else-if="invoice.projects && invoice.projects.length > 1">
                      Multiple Projects (
                      <span
                        v-for="(project, index) in invoice.projects"
                        :key="project.id"
                      >
                        <a
                          :href="getProjectUrl(project.id)"
                          target="_blank"
                          class="project-link"
                        >{{ project.title }}</a><span v-if="index < invoice.projects.length - 1">, </span>
                      </span>
                      )
                    </span>
                    <span v-else>{{ invoice.project_title }}</span>
                  </td>
                  <td>{{ invoice.client_name }}</td>
                  <td>{{ invoice.issue_date }}</td>
                  <td>{{ invoice.due_date }}</td>
                  <td>
                    <span class="amount-chip">
                      {{ invoice.currency }} {{ formatAmount(invoice.total_amount) }}
                    </span>
                  </td>
                  <td>{{ invoice.created_by_name }}</td>
                  <td class="text-center">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      title="Download PDF"
                      @click="downloadInvoice(invoice)"
                    >
                      <i class="fa fa-download" />
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary ms-1"
                      title="Update Status"
                      @click="openStatusModal(invoice)"
                    >
                      <i class="fa fa-edit" />
                    </button>
                  </td>
                </tr>
                <tr
                  v-if="isInvoiceExpanded(invoice.id) && invoice.items && invoice.items.length > 0"
                  :key="'items-' + invoice.id"
                  class="invoice-items-row"
                >
                  <td colspan="11">
                    <div class="invoice-items-panel">
                      <table class="table invoice-items-table mb-0 align-middle">
                        <thead>
                          <tr>
                            <th style="width: 4%;">
                              #
                            </th>
                            <th style="width: 22%;">
                              Type
                            </th>
                            <th style="width: 28%;">
                              Expert
                            </th>
                            <th style="width: 14%;">
                              Credit(s)
                            </th>
                            <th style="width: 12%;">
                              Rate
                            </th>
                            <th
                              class="text-end"
                              style="width: 20%;"
                            >
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(item, index) in invoice.items"
                            :key="item.id"
                          >
                            <td>{{ index + 1 }}</td>
                            <td>{{ item.type }}</td>
                            <td>
                              <a
                                v-if="item.expert_id"
                                :href="getExpertUrl(item.expert_id)"
                                target="_blank"
                                class="expert-link"
                              >{{ item.expert_name }}</a>
                              <span v-else>{{ item.expert_name }}</span>
                            </td>
                            <td>{{ item.credits == null ? '-' : formatAmount(item.credits) }}</td>
                            <td>{{ item.rate == null ? '-' : formatCurrencyAmount(invoice.currency, item.rate) }}</td>
                            <td class="text-end fw-semibold">
                              {{ formatCurrencyAmount(invoice.currency, item.amount) }}
                            </td>
                          </tr>
                          <tr class="invoice-items-total">
                            <td colspan="4">
                              {{ invoice.items.length }} item{{ invoice.items.length === 1 ? '' : 's' }}
                            </td>
                            <td class="text-end fw-semibold">
                              {{ invoice.currency }}
                            </td>
                            <td class="text-end fw-semibold">
                              {{ formatCurrencyAmount(invoice.currency, invoice.total_amount) }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <div
      v-else
      class="empty-invoices card border-0 text-center shadow-sm"
    >
      <div class="card-body py-5">
        <span class="empty-icon mb-3">
          <i class="fa fa-file-invoice-dollar" />
        </span>
        <h3 class="h5 mb-2">
          No invoices found
        </h3>
        <p class="text-muted mb-0">
          No invoices have been generated yet. They will appear here once created from the management page.
        </p>
      </div>
    </div>

    <p
      v-if="!loading && showAll && totalInvoices > 0"
      class="text-muted small text-center mb-0 mt-3"
    >
      Showing all {{ totalInvoices }} results
    </p>

    <invoice-pagination
      v-if="!loading && totalInvoices > 0 && !showAll"
      :current-page="currentPage"
      :total="totalInvoices"
      :per-page="perPage"
      @page-change="onPageChange"
      @per-page-change="onPerPageChange"
    />

    <invoice-status-modal
      v-if="statusModal.show"
      :invoice="statusModal.invoice"
      @invoice-updated="onInvoiceUpdated"
      @close="closeStatusModal"
    />

    <div
      v-if="selectedInvoiceIds.length > 0"
      class="invoice-bulk-tray shadow"
    >
      <span class="bulk-count">
        {{ selectedInvoiceIds.length }} invoice{{ selectedInvoiceIds.length === 1 ? '' : 's' }} selected
      </span>
      <select
        v-model="bulkStatus"
        class="form-select form-select-sm bulk-status-select"
        :disabled="bulkSaving"
      >
        <option :value="null">
          &mdash; Select status &mdash;
        </option>
        <option value="">
          Unpaid
        </option>
        <option value="send_to_client">
          Sent to Client
        </option>
        <option value="paid">
          Paid
        </option>
      </select>
      <button
        type="button"
        class="btn btn-sm btn-primary"
        :disabled="bulkSaving || bulkStatus === null"
        @click="applyBulkStatus"
      >
        <span
          v-if="bulkSaving"
          class="spinner-border spinner-border-sm me-1"
        />
        Apply
      </button>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        :disabled="bulkSaving"
        @click="clearSelection"
      >
        Clear
      </button>
    </div>
  </section>
</template>

<script>
import InvoiceFilterBar from "./InvoiceFilterBar.vue";
import InvoicePagination from "./InvoicePagination.vue";
import InvoiceStatusModal from "./InvoiceStatusModal.vue";

export default {
    name: "InvoiceHistoryPage",
    components: {
        InvoiceFilterBar,
        InvoicePagination,
        InvoiceStatusModal,
    },
    data() {
        return {
            invoices: [],
            loading: false,
            search: "",
            clientFilter: "",
            statusFilter: "__all__",
            dateFrom: "",
            dateTo: "",
            perPage: 10,
            currentPage: 1,
            showAll: false,
            totalInvoices: 0,
            expandedInvoiceIds: [],
            selectedInvoiceIds: [],
            bulkStatus: null,
            savingStatusIds: {},
            bulkSaving: false,
            statusModal: {
                show: false,
                invoice: null,
            },
        };
    },
    computed: {
        allSelected() {
            return this.invoices.length > 0
                && this.invoices.every((inv) => this.selectedInvoiceIds.includes(inv.id));
        },
        someSelected() {
            return this.selectedInvoiceIds.length > 0 && !this.allSelected;
        },
    },
    mounted() {
        this.loadHistory();
    },
    methods: {
        loadHistory() {
            this.loading = true;
            this.expandedInvoiceIds = [];
            this.selectedInvoiceIds = [];
            this.bulkStatus = null;
            const params = {
                search: this.search,
                per_page: this.perPage,
                page: this.currentPage,
                client_id: this.clientFilter || "",
                ...(this.statusFilter !== "__all__" ? { invoice_status: this.statusFilter } : {}),
                date_from: this.dateFrom,
                date_to: this.dateTo,
                ...(this.showAll ? { show_all: 1 } : {}),
            };
            axios.get("/admin/invoice/history/list", { params })
                .then(({ data }) => {
                    this.invoices = data.data;
                    this.totalInvoices = data.meta.total;
                })
                .finally(() => { this.loading = false; });
        },
        onFilterChange(filters) {
            this.search = filters.search;
            this.clientFilter = filters.clientId;
            this.statusFilter = filters.invoiceStatus ?? "__all__";
            this.dateFrom = filters.dateFrom || "";
            this.dateTo = filters.dateTo || "";
            this.showAll = !!filters.showAll;
            this.currentPage = 1;
            this.loadHistory();
        },
        downloadInvoice(invoice) {
            window.open("/admin/invoice/" + invoice.id + "/download", "_blank");
        },
        getProjectUrl(projectId) {
            return `/admin/project/details/${projectId}`;
        },
        getExpertUrl(expertId) {
            return `/admin/experts/view/${expertId}`;
        },
        onPageChange(page) {
            this.currentPage = page;
            this.loadHistory();
        },
        onPerPageChange(perPage) {
            this.perPage = perPage;
            this.currentPage = 1;
            this.loadHistory();
        },
        toggleInvoiceExpand(invoiceId) {
            const id = Number(invoiceId);
            const index = this.expandedInvoiceIds.indexOf(id);

            if (index === -1) {
                this.expandedInvoiceIds.push(id);
            } else {
                this.expandedInvoiceIds.splice(index, 1);
            }
        },
        isInvoiceExpanded(invoiceId) {
            return this.expandedInvoiceIds.includes(Number(invoiceId));
        },
        formatAmount(value) {
            return Number(value || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        formatCurrencyAmount(currency, value) {
            const prefix = currency === "USD" ? "$ " : `${currency} `;

            return prefix + this.formatAmount(value);
        },
        openStatusModal(invoice) {
            this.statusModal = { show: true, invoice };
        },
        closeStatusModal() {
            this.statusModal.show = false;
        },
        onInvoiceUpdated(updated) {
            const invoice = this.invoices.find((inv) => inv.id === updated.id);
            if (invoice) {
                invoice.invoice_number = updated.invoice_number;
                invoice.invoice_number_formatted = updated.invoice_number_formatted;
                invoice.issue_date = this.formatDate(updated.issue_date);
                invoice.due_date = this.formatDate(updated.due_date);
                invoice.total_amount = updated.total_amount;
                invoice.invoice_status = updated.invoice_status;
                invoice.client_name = updated.client_name;
                if (updated.items) {
                    invoice.items = updated.items;
                }
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr + 'T00:00:00');
            return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
        },
        toggleSelectAll(checked) {
            if (checked) {
                this.selectedInvoiceIds = this.invoices.map((inv) => inv.id);
            } else {
                this.selectedInvoiceIds = [];
            }
        },
        toggleRowSelect(id, checked) {
            const idx = this.selectedInvoiceIds.indexOf(id);
            if (checked && idx === -1) {
                this.selectedInvoiceIds.push(id);
            } else if (!checked && idx !== -1) {
                this.selectedInvoiceIds.splice(idx, 1);
            }
        },
        onStatusChange(invoice, event) {
            const previous = invoice.invoice_status;
            const next = event.target.value;
            this.$set(this.savingStatusIds, invoice.id, true);
            axios.patch(`/admin/invoice/${invoice.id}/status`, { invoice_status: next })
                .then(() => {
                    invoice.invoice_status = next;
                })
                .catch(() => {
                    event.target.value = previous;
                })
                .finally(() => {
                    this.$delete(this.savingStatusIds, invoice.id);
                });
        },
        clearSelection() {
            this.selectedInvoiceIds = [];
            this.bulkStatus = null;
        },
        applyBulkStatus() {
            if (this.bulkStatus === null || this.selectedInvoiceIds.length === 0) return;
            const selectedInvoiceIds = [...this.selectedInvoiceIds];
            const invoiceStatus = this.bulkStatus;
            this.bulkSaving = true;
            axios.patch("/admin/invoice/bulk-status", {
                ids: selectedInvoiceIds,
                invoice_status: invoiceStatus,
            })
                .then(() => {
                    this.invoices.forEach((inv) => {
                        if (selectedInvoiceIds.includes(inv.id)) {
                            inv.invoice_status = invoiceStatus;
                        }
                    });
                    this.selectedInvoiceIds = [];
                    this.bulkStatus = null;
                })
                .finally(() => {
                    this.bulkSaving = false;
                });
        },
    },
};
</script>

<style scoped>
.invoice-management-page {
    background:
        radial-gradient(circle at 96% 3%, rgba(76, 120, 221, .09), transparent 26rem),
        #f6f8f8;
    border-radius: 18px;
    min-height: calc(100vh - 150px);
    padding: 1.75rem;
}

.invoice-page-header h1 {
    color: #1F2937;
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: -.035em;
}

.eyebrow {
    color: #4c78dd;
    font-size: .7rem;
    font-weight: 700;
    letter-spacing: .16em;
    text-transform: uppercase;
}

.history-table-card {
    border-radius: 14px;
}

.history-table-card thead th {
    background: #f7f9f8;
    border-bottom-width: 1px;
    color: #4B5563;
    font-size: .78rem;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    white-space: nowrap;
}

.history-table-card tbody td {
    font-size: .9rem;
    vertical-align: middle;
}

.expand-cell {
    width: 40px;
}

.expand-toggle {
    color: #7e8c92;
    line-height: 1;
    text-decoration: none;
}

.expand-toggle:hover,
.expand-toggle:focus {
    color: #4c78dd;
}

.chevron {
    font-size: .85rem;
}

.invoice-row-expanded td {
    border-bottom-color: transparent;
}

.invoice-items-row td {
    border-top: 0;
    padding-top: 0;
}

.invoice-items-panel {
    background: #f7f9f8;
    border-radius: 10px;
    margin: 0 .75rem .75rem;
    padding: .75rem 1rem 1rem;
}

.invoice-items-table {
    font-size: .82rem;
}

.invoice-items-table thead th {
    background: transparent;
    border-bottom: 1px solid #e8ecec;
    color: #718088;
    font-size: .72rem;
    font-weight: 700;
    letter-spacing: .06em;
    padding: .55rem .75rem;
    text-transform: uppercase;
    white-space: nowrap;
}

.invoice-items-table tbody td {
    border-color: #e8ecec;
    padding: .65rem .75rem;
}

.invoice-items-total td {
    background: #eef2fb;
    border-top: 1px solid #dbe3f7;
    color: #1F2937;
}

.amount-chip {
    background: #eef2fb;
    border-radius: 6px;
    color: #1F2937;
    display: inline-block;
    font-weight: 700;
    padding: .25rem .6rem;
    white-space: nowrap;
}

.project-link {
    color: #4c78dd;
    font-weight: 600;
    text-decoration: none;
}

.project-link:hover {
    color: #3a5fc7;
    text-decoration: underline;
}

.expert-link {
    color: #4c78dd;
    font-weight: 600;
    text-decoration: none;
}

.expert-link:hover {
    color: #3a5fc7;
    text-decoration: underline;
}

.empty-invoices {
    border-radius: 14px;
}

.empty-icon {
    align-items: center;
    background: #eef2fb;
    border-radius: 50%;
    color: #4c78dd;
    display: inline-flex;
    font-size: 1.4rem;
    height: 62px;
    justify-content: center;
    width: 62px;
}

.invoice-status-select {
    width: auto;
}

.invoice-bulk-tray {
    align-items: center;
    background: #fff;
    border-radius: 12px;
    bottom: 1.5rem;
    box-shadow: 0 .5rem 1.5rem rgba(31, 41, 55, .18);
    display: flex;
    gap: .75rem;
    left: 50%;
    padding: .75rem 1.25rem;
    position: fixed;
    transform: translateX(-50%);
    z-index: 1030;
}

.invoice-bulk-tray .bulk-count {
    color: #1F2937;
    font-size: .9rem;
    font-weight: 600;
    white-space: nowrap;
}

.invoice-bulk-tray .bulk-status-select {
    min-width: 10rem;
}

@media (max-width: 767.98px) {
    .invoice-management-page {
        padding: 1rem;
    }

    .invoice-page-header h1 {
        font-size: 1.65rem;
    }

    .history-table-card .table-responsive {
        font-size: .82rem;
    }
}
</style>
